import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { Button, ButtonSpinner, ButtonText } from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { Input, InputField } from "@/components/ui/input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import {
  getOrgWalletControllerListQueryKey,
  useOrgWalletControllerCreate,
  useOrgWalletControllerDelete,
  useOrgWalletControllerUpdate,
} from "@/generated/api/api";
import { WalletDTO } from "@/generated/api/models";
import { useToast } from "@/utils/useToast";
import { InterfaceAlertDialogProps } from "@gluestack-ui/alert-dialog/lib/types";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react-native";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Keyboard } from "react-native";
import { z } from "zod";

const WalletSchema = z.object({
  name: z.string().min(1, "Wallet's name is required"),
});

type WalletSchemaType = z.infer<typeof WalletSchema>;

export function RestaurantWalletModalCreate(
  props: { organizationId: string } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >
) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WalletSchemaType>({
    resolver: zodResolver(WalletSchema),
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useOrgWalletControllerCreate({
    mutation: {
      onSuccess: (data, variables, context) => {
        showToast("success", "Wallet has been successfully created");
        // TODO: setQueryData
        queryClient.invalidateQueries({
          queryKey: getOrgWalletControllerListQueryKey(props.organizationId),
        });
        props.onClose?.();
        reset();
      },
    },
  });

  const onSubmit = (data: WalletSchemaType) => {
    return mutateAsync({
      organizationId: props.organizationId,
      data,
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Create wallet</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4">
          <FormControl isInvalid={!!errors?.name} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Wallet's name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="name"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Enter name"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.name?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Create</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RestaurantWalletModalEdit(
  props: { organizationId: string; wallet: WalletDTO } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >
) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WalletSchemaType>({
    resolver: zodResolver(WalletSchema),
    defaultValues: {
      name: props.wallet.name,
    },
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  useEffect(() => {
    reset({ name: props.wallet.name });
  }, [props.wallet.name]);

  const { mutateAsync } = useOrgWalletControllerUpdate({
    mutation: {
      onSuccess: (data, variables, context) => {
        showToast("success", "Wallet has been successfully updated");
        // TODO: setQueryData
        queryClient.invalidateQueries({
          queryKey: getOrgWalletControllerListQueryKey(props.organizationId),
        });
        props.onClose?.();
        reset();
      },
    },
  });

  const onSubmit = (data: WalletSchemaType) => {
    return mutateAsync({
      organizationId: props.organizationId,
      walletId: props.wallet.id,
      data,
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-0.5">
          <Heading>Edit wallet</Heading>
        </ModalHeader>
        <ModalBody className="mb-4">
          <FormControl isInvalid={!!errors?.name} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Wallet's name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="name"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Enter name"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.name?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RestaurantWalletModalDelete({
  organizationId,
  wallet,
  ...rest
}: { organizationId: string; wallet: WalletDTO } & Pick<
  InterfaceAlertDialogProps,
  "isOpen" | "onClose"
>) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutate, isPending } = useOrgWalletControllerDelete({
    mutation: {
      onSuccess: (data, variables, context) => {
        showToast("success", "Wallet has been successfully deleted");
        // TODO: setQueryData
        queryClient.invalidateQueries({
          queryKey: getOrgWalletControllerListQueryKey(organizationId),
        });
        rest.onClose?.();
      },
    },
  });

  const balance = wallet.balance + wallet.holdBalance;

  return (
    <AlertDialog {...rest}>
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading className="text-typography-950 font-semibold" size="md">
            Are you sure you want to delete wallet {wallet.name}?
          </Heading>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3 mb-4">
          {balance > 0 ? (
            <Text size="sm" bold>
              Unable to delete this wallet, because of positive balance. Please
              transfer funds first and then try again.
            </Text>
          ) : (
            <Text size="sm">
              Deleting this wallet will remove it from your personal area.{" "}
            </Text>
          )}
        </AlertDialogBody>
        <AlertDialogFooter className="">
          <Button
            variant="outline"
            action="secondary"
            onPress={rest.onClose}
            size="sm"
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            size="sm"
            isDisabled={isPending || balance > 0}
            onPress={() => mutate({ organizationId, walletId: wallet.id })}
          >
            <ButtonText>Delete</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
